<template>
  <div class="billing-status">
    <!-- Díjbekérő -->
    <div 
      class="billing-item" 
      :class="{ 'clickable': hasProformaInvoice && projectId }"
      v-if="hasProformaInvoice"
      @click="downloadInvoice('proforma')"
    >
      <i class="fas fa-file-invoice" :class="proformaClass"></i>
      <span class="billing-label">Díjbekérő</span>
      <span class="billing-badge" :class="proformaClass">
        {{ proformaInvoicePaid ? 'Fizetve' : 'Fizetésre vár' }}
      </span>
    </div>

    <!-- Előleg számla -->
    <div 
      class="billing-item" 
      :class="{ 'clickable': hasAdvanceInvoice && projectId }"
      v-if="hasAdvanceInvoice"
      @click="downloadInvoice('advance')"
    >
      <i class="fas fa-coins" :class="advanceClass"></i>
      <span class="billing-label">Előleg</span>
      <span class="billing-badge" :class="advanceClass">
        {{ getAdvanceStatus }}
      </span>
    </div>

    <!-- Végszámla -->
    <div 
      class="billing-item" 
      :class="{ 'clickable': hasFinalInvoice && projectId }"
      v-if="hasFinalInvoice"
      @click="downloadInvoice('final')"
    >
      <i class="fas fa-file-invoice-dollar" :class="finalClass"></i>
      <span class="billing-label">Végszámla</span>
      <span class="billing-badge" :class="finalClass">
        {{ finalInvoicePaid ? 'Fizetve' : 'Fizetésre vár' }}
      </span>
    </div>

    <!-- Ha nincs még számla -->
    <div class="billing-item billing-empty" v-if="!hasAnyInvoice">
      <i class="fas fa-hourglass-half text-muted"></i>
      <span class="billing-label text-muted">Nincs számla</span>
    </div>

    <!-- Összértékek -->
    <div class="billing-summary" v-if="showSummary && projectBalance">
      <div class="summary-item" v-if="projectBalance.waiting_on_money.gross > 0">
        <span class="summary-label">Várakozik:</span>
        <span class="summary-value text-warning">{{ formatCurrency(projectBalance.waiting_on_money.gross) }}</span>
      </div>
      <div class="summary-item" v-if="projectBalance.paid.gross > 0">
        <span class="summary-label">Befizetve:</span>
        <span class="summary-value text-success">{{ formatCurrency(projectBalance.paid.gross) }}</span>
      </div>
      <div class="summary-item" v-if="projectBalance.project_sum_value.gross > 0">
        <span class="summary-label">Összérték:</span>
        <span class="summary-value">{{ formatCurrency(projectBalance.project_sum_value.gross) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService'

export default {
  name: 'BillingStatus',
  props: {
    hasProformaInvoice: {
      type: Boolean,
      default: false
    },
    proformaInvoicePaid: {
      type: Boolean,
      default: false
    },
    hasAdvanceInvoice: {
      type: Boolean,
      default: false
    },
    hasFinalInvoice: {
      type: Boolean,
      default: false
    },
    finalInvoicePaid: {
      type: Boolean,
      default: false
    },
    projectBalance: {
      type: Object,
      default: null
    },
    showSummary: {
      type: Boolean,
      default: true
    },
    projectId: {
      type: [String, Number],
      default: null
    }
  },
  computed: {
    hasAnyInvoice() {
      return this.hasProformaInvoice || this.hasAdvanceInvoice || this.hasFinalInvoice
    },
    proformaClass() {
      return this.proformaInvoicePaid ? 'status-paid' : 'status-pending'
    },
    advanceClass() {
      if (!this.projectBalance) return 'status-pending'
      
      const paidGross = this.projectBalance.paid.gross
      const waitingGross = this.projectBalance.waiting_on_money.gross
      
      if (paidGross > 0 && waitingGross === 0) {
        return 'status-paid'
      }
      return 'status-pending'
    },
    finalClass() {
      return this.finalInvoicePaid ? 'status-paid' : 'status-pending'
    },
    getAdvanceStatus() {
      if (!this.projectBalance) return 'Nincs adat'
      
      const paidGross = this.projectBalance.paid.gross
      const waitingGross = this.projectBalance.waiting_on_money.gross
      
      if (paidGross > 0 && waitingGross === 0) {
        return 'Fizetve'
      }
      if (waitingGross > 0) {
        return 'Részben fizetve'
      }
      return 'Fizetésre vár'
    }
  },
  methods: {
    formatCurrency(amount) {
      if (!amount) return '-'
      return new Intl.NumberFormat('hu-HU', {
        style: 'currency',
        currency: 'HUF',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount)
    },
    async downloadInvoice(invoiceType) {
      if (!this.projectId) return
      
      try {
        const resp = await apiService.get(`/projects/${this.projectId}/invoices/${invoiceType}/download`, { responseType: 'blob' })

        // Handle axios-like or fetch-like result
        let blob, filename = `${invoiceType}-${this.projectId}.zip`
        if (resp && resp.data instanceof Blob) {
          blob = resp.data
          // Extract filename from Content-Disposition header
          const contentDisposition = resp.headers['content-disposition']
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['"]/g, '')
            }
          }
        } else if (resp instanceof Blob) {
          blob = resp
        } else if (resp && typeof resp.blob === 'function') {
          blob = await resp.blob()
          // Try to get headers if available
          if (resp.headers && resp.headers.get) {
            const contentDisposition = resp.headers.get('content-disposition')
            if (contentDisposition) {
              const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
              if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1].replace(/['"]/g, '')
              }
            }
          }
        } else {
          // unknown response shape => try to create blob from resp.data
          const data = resp && resp.data ? resp.data : resp
          blob = new Blob([data])
        }

        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        console.error('Error downloading invoice:', err)
        // fallback: open endpoint in new tab (may trigger browser download)
        try {
          window.open(`/projects/${this.projectId}/invoices/${invoiceType}/download`, '_blank')
        } catch (e) {
          // ignore
        }
      }
    }
  }
}
</script>

<style scoped>
.billing-status {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 13px;
}

.billing-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: #f8f9fa;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.billing-item:hover {
  background: #e9ecef;
}

.billing-item.billing-empty {
  opacity: 0.7;
}

.billing-item i {
  font-size: 14px;
  min-width: 16px;
  text-align: center;
}

.billing-label {
  flex: 1;
  font-weight: 500;
  color: #495057;
}

.billing-badge {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
}

/* Státusz színek */
.status-paid {
  color: #28a745 !important;
}

.status-paid.billing-badge {
  background: #d4edda;
  color: #155724;
}

.status-pending {
  color: #ffc107 !important;
}

.status-pending.billing-badge {
  background: #fff3cd;
  color: #856404;
}

.text-muted {
  color: #6c757d !important;
}

/* Összefoglaló */
.billing-summary {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.summary-label {
  color: #6c757d;
  font-weight: 500;
}

.summary-value {
  font-weight: 600;
  color: #495057;
}

.summary-value.text-success {
  color: #28a745 !important;
}

.summary-value.text-warning {
  color: #ffc107 !important;
}

/* Kompakt nézet */
.billing-status.compact .billing-item {
  padding: 4px 8px;
  gap: 6px;
}

.billing-status.compact .billing-label {
  font-size: 11px;
}

.billing-status.compact .billing-badge {
  padding: 2px 6px;
  font-size: 10px;
}

/* Responsive */
@media (max-width: 768px) {
  .billing-item {
    font-size: 12px;
  }
  
  .billing-badge {
    font-size: 10px;
    padding: 2px 6px;
  }
}

.billing-item.clickable {
  cursor: pointer;
}

.billing-item.clickable:hover {
  background: #e9ecef;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>